
import React from 'react';
import { PlanDetail } from '../types';
import { CURRENCIES } from '../constants';
import { useSettings } from './SettingsContext';

interface PlanCardProps {
  plan: PlanDetail;
  billingCycle: 'monthly' | 'quarterly' | 'annually';
  onSelect: (plan: PlanDetail) => void;
  onToggleStatus: (id: string) => void;
  onEdit: (plan: PlanDetail) => void;
}

const PlanCard: React.FC<PlanCardProps> = ({ plan, billingCycle, onSelect, onToggleStatus, onEdit }) => {
  const { settings } = useSettings();

  const quarterlyDiscount = plan.quarterlyDiscountPercentage ?? settings.checkoutConfig?.quarterlyDiscountPercentage ?? 0;
  const yearlyDiscount = plan.yearlyDiscountPercentage ?? settings.checkoutConfig?.yearlyDiscountPercentage ?? 0;

  let effectivePrice = plan.price;
  if (billingCycle === 'quarterly' && quarterlyDiscount > 0) {
    effectivePrice = Math.round(plan.price * (1 - quarterlyDiscount / 100));
  } else if (billingCycle === 'annually' && yearlyDiscount > 0) {
    effectivePrice = Math.round(plan.price * (1 - yearlyDiscount / 100));
  }

  const symbol = CURRENCIES.find(c => c.code === plan.currency)?.symbol || '₹';

  const handleCopyId = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(plan.id);
  };

  return (
    <div 
      className={`group relative flex flex-col p-8 rounded-3xl border transition-all duration-300 ${
        !plan.isActive ? 'opacity-60 grayscale-[0.5] border-slate-200' :
        plan.isPopular 
          ? 'border-indigo-600 bg-white ring-8 ring-indigo-50 shadow-2xl scale-105 z-10' 
          : 'border-slate-200 bg-white shadow-sm hover:shadow-xl hover:-translate-y-1'
      }`}
    >
      <div className="absolute top-4 right-4 z-20 flex items-center gap-2">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onEdit(plan); }}
          title="Edit Plan & Add-ons"
          className="p-1.5 rounded-full bg-slate-100 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 transition-all shadow-xs"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
            <path d="m15 5 4 4"/>
          </svg>
        </button>
        <button 
          onClick={(e) => { e.stopPropagation(); onToggleStatus(plan.id); }}
          title={plan.isActive ? "Deactivate Plan" : "Activate Plan"}
          className={`w-10 h-6 rounded-full relative p-1 transition-all duration-200 ${plan.isActive ? 'bg-emerald-500' : 'bg-slate-300'}`}
        >
          <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 transform ${plan.isActive ? 'translate-x-4' : 'translate-x-0'}`}></div>
        </button>
      </div>

      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <span className={`inline-block px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest ${
            plan.isPopular ? 'bg-indigo-100 text-indigo-700' : plan.color
          }`}>
            {plan.tier}
          </span>
          {plan.addons && plan.addons.length > 0 && (
            <span className="px-2 py-1 bg-purple-50 text-purple-700 border border-purple-100 text-[10px] font-black rounded-lg uppercase tracking-tight">
              +{plan.addons.length} Add-on{plan.addons.length > 1 ? 's' : ''}
            </span>
          )}
          {!plan.isActive && (
            <span className="px-2 py-1 bg-slate-200 text-slate-600 text-[10px] font-black rounded-lg uppercase tracking-widest">
              Inactive
            </span>
          )}
        </div>
        <h3 className="text-2xl font-bold text-slate-900 mb-2">{plan.tier}</h3>
        
        <div className="flex items-center gap-1.5 mb-3 group/id cursor-pointer w-fit" onClick={handleCopyId} title="Copy Plan ID">
           <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-slate-300 group-hover/id:text-indigo-500 transition-colors"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
           <code className="text-[10px] font-mono text-slate-400 bg-slate-50 px-2 py-0.5 rounded border border-slate-100 group-hover/id:border-indigo-100 group-hover/id:text-indigo-600 transition-colors">
             {plan.id}
           </code>
        </div>
      </div>

      <div className="mb-8">
        <div className="flex items-baseline gap-1">
          <span className="text-5xl font-black text-slate-900 tracking-tighter">
            {symbol}{effectivePrice.toLocaleString()}
          </span>
          <div className="flex flex-col">
             <span className="text-slate-400 font-bold text-xs uppercase tracking-widest">/mo</span>
          </div>
        </div>
        
        {/* Metered Pricing Display */}
        {plan.meteredRules && plan.meteredRules.length > 0 && (
          <div className="mt-4 space-y-1">
            {plan.meteredRules.map(rule => (
              <div key={rule.id} className="flex items-center gap-2 text-[10px] font-black text-indigo-600 uppercase tracking-widest">
                <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse"></span>
                + {symbol}{rule.pricePerUnit} / {rule.unitName}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mb-6 p-3 bg-slate-50 rounded-xl border border-slate-100 flex items-center gap-3">
         <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a10 10 0 1 0 10 10H12V2Z"/><path d="M12 2a10 10 0 0 1 10 10h-7.5l-3-3L12 2Z"/><path d="M12 12V2.112A10 10 0 0 1 21.888 12H12Z"/></svg>
         </div>
         <div>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Included AI Usage</p>
            <p className="text-sm font-bold text-slate-900">{plan.aiCredits ? plan.aiCredits.toLocaleString() : 0} Credits / mo</p>
         </div>
      </div>

      <div className="flex-grow space-y-4 mb-10">
        <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Platform Access</p>
        <ul className="space-y-3">
          {plan.features.map((feature, idx) => (
            <li key={idx} className="flex items-start gap-3 text-sm text-slate-600 leading-tight">
              <svg className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
              <span className="group-hover:text-slate-900 transition-colors">{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      <button 
        onClick={() => onSelect(plan)}
        disabled={!plan.isActive}
        className={`w-full py-4 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] transition-all duration-300 ${
          !plan.isActive ? 'bg-slate-200 text-slate-400 cursor-not-allowed' :
          plan.isPopular 
            ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-indigo-200 shadow-xl active:scale-95' 
            : 'bg-slate-900 text-white hover:bg-slate-800 active:scale-95'
        }`}
      >
        {plan.isActive ? 'Deploy Instance' : 'Tier Retired'}
      </button>
    </div>
  );
};

export default PlanCard;
